import React, { useState, useEffect } from 'react'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'
import ConfigBillingRegionItem from './ConfigBillingRegionItem'

const ConfigBillingServiceItem = (props) => {
  const {
    title,
    service,
    expandedPanel,
    handleChangeExpandedPanel,
    backendUserData,
    setBackendUserData,
    getRegionConfig,
    backendIsIdentityProviderSelected,
    billingServicesConfigurable,
    errors,
    setErrors,
    submitted,
    validateFormFields,
    appCredentialSecret,
    setAppCredentialSecret,
  } = props
  const theme = useTheme()

  const [expandedSubPanel, setExpandedSubPanel] = useState(0)
  const [enableServiceBilling, setEnableServiceBilling] = useState(true)
  const [regionConfigs, setRegionConfigs] = useState([])
  const [isIdentityProvider, setIsIdentityProvider] = useState(false)

  const accordionStyle = {
    background: 'none',
    boxShadow: 'none'
  }

  const summaryStyle = {
    background: theme.palette.violetsAreBlue,
    color: 'white',
    fontSize: '18px',
  }

  const detailsStyle = {
    border: `${theme.palette.violetsAreBlue} 1px solid`,
    background: 'white',
    boxShadow: 'none',
    padding: '0px',
  }

  const regionSummaryStyle = {
    borderBottom: `${theme.palette.oxfordBlue} 1px solid`,
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
  }

  const serviceCheckboxStyle = {
    color: 'white',

    '&.Mui-checked': {
      color: 'white',
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const regionCheckboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const getRegionChecked = (region_name) => {
    const region_object = regionConfigs.find(item => item.region_name === region_name)
    return (region_object && region_object.enable_billing) ? true : false
  }

  const handleStopPropagation = (event) => {
    event.stopPropagation()
  } 

  const handleChangeEnableServiceBilling = (value) => {
    setEnableServiceBilling(value)

    setBackendUserData(prev => {
      let new_region_configs = []

      prev.forEach(item => {
        if (item.service === service) {
          new_region_configs.push({ ...item, enable_billing: value })
        } else {
          new_region_configs.push(item)
        }
      })

      return new_region_configs
    })
  }
  
  const handleChangeEnableRegionBilling = (value, field_key) => {
    setBackendUserData(prev => {
      let new_region_configs = []

      prev.forEach(item => {
        if (item.service === service && item.region_name === field_key) {
          new_region_configs.push({ ...item, enable_billing: value })
        } else {
          new_region_configs.push(item)
        }
      })

      return new_region_configs
    })
  }

  const handleClickAddRegion = (event) => {    
    if (expandedPanel === service) {
      event.stopPropagation()
    }

    setBackendUserData(prev => [
      ...prev,
      {
        service,
        ...getRegionConfig(service, title.value, regionConfigs.length + 1)
      }      
    ])
  }

  const handleClickRemoveRegion = (event, region_name) => {
    event.stopPropagation()
    setBackendUserData(prev => prev.filter(item => (item.service !== service || item.region_name !== region_name)))
  }

  const handleChangeExpandedSubPanel = (panel) => (event, newExpanded) => {
    setExpandedSubPanel(newExpanded ? panel : false)
  }

  useEffect(() => {
    const identity_service = backendUserData.find(item => item.service === service && item.is_identity_provider)
    setIsIdentityProvider(identity_service ? true : false)
    setRegionConfigs(backendUserData.filter(item => item.service === service))
  }, [backendUserData, service])

  useEffect(() => {
    const region_with_billing = regionConfigs.find(item => item.enable_billing)
    setEnableServiceBilling(region_with_billing ? true : false)
  }, [regionConfigs])
  
  return (
    <Accordion
      expanded={expandedPanel === service}
      onChange={handleChangeExpandedPanel(service)}
      sx={accordionStyle}
    >
      <AccordionSummary
        expandIcon={<ArrowDropDownIcon sx={{ color: 'white' }} />}
        sx={{
          ...summaryStyle,
          borderRadius: expandedPanel === service ? '10px 10px 0px 0px' : '0px',
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-start'
          spacing={1}
        >
          <Box onClick={handleStopPropagation}>
            <CustomCheckboxField
              checked={enableServiceBilling}
              handleChange={handleChangeEnableServiceBilling}
              sx={serviceCheckboxStyle}
            />
          </Box>
          <Box>{title.value}</Box>
          {
            (!isIdentityProvider && billingServicesConfigurable) &&
            <IconButton sx={{ color: 'white' }} onClick={handleClickAddRegion}>
              <AddCircleOutlineIcon />
            </IconButton>
          }
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={detailsStyle}>
        {
          regionConfigs.length === 1 && regionConfigs.map((item, index) => (
            <ConfigBillingRegionItem
              key={index}
              {...item}
              service={service}
              backendUserData={backendUserData}
              setBackendUserData={setBackendUserData}
              backendIsIdentityProviderSelected={backendIsIdentityProviderSelected}
              billingServicesConfigurable={billingServicesConfigurable}
              errors={errors}
              setErrors={setErrors}
              submitted={submitted}
              validateFormFields={validateFormFields}
              appCredentialSecret={appCredentialSecret}
              setAppCredentialSecret={setAppCredentialSecret}
            />
          ))
        }

        {
          regionConfigs.length > 1 && regionConfigs.map((item, index) => (
            <Accordion
              key={index}
              expanded={expandedSubPanel === index}
              onChange={handleChangeExpandedSubPanel(index)}
              sx={accordionStyle}
            >
              <AccordionSummary
                expandIcon={<ArrowDropDownIcon />}
                sx={regionSummaryStyle}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                  spacing={1}
                >
                  <Box onClick={handleStopPropagation}>
                    <CustomCheckboxField
                      checked={getRegionChecked(item.region_name)}
                      handleChange={handleChangeEnableRegionBilling}
                      field_key={item.region_name}
                      sx={regionCheckboxStyle}
                    />
                  </Box>
                  <Box>{item.region_name}</Box>
                  {
                    (index > 0 && billingServicesConfigurable) &&
                    <IconButton onClick={(event) => handleClickRemoveRegion(event, item.region_name)}>
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  }
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <ConfigBillingRegionItem
                  key={index}
                  {...item}
                  service={service}
                  backendUserData={backendUserData}
                  setBackendUserData={setBackendUserData}
                  backendIsIdentityProviderSelected={backendIsIdentityProviderSelected}
                  billingServicesConfigurable={billingServicesConfigurable}
                  errors={errors}
                  setErrors={setErrors}
                  submitted={submitted}
                  validateFormFields={validateFormFields}
                  appCredentialSecret={appCredentialSecret}
                  setAppCredentialSecret={setAppCredentialSecret}
                />
              </AccordionDetails>              
            </Accordion>            
          ))
        }
      </AccordionDetails>
    </Accordion>
  )
}

export default ConfigBillingServiceItem